import { useRef, useState } from "react";
import { ReactComponent as MasterCardLogo } from "../../assets/Mastercard method.svg";
import { ReactComponent as VisaLogo } from "../../assets/Visa method.svg";
import { ReactComponent as RwFlag } from "../../assets/rw.svg";
import InputField from "../../components/inputField";
import ConfirmModal from "./confirmModal";
import ErrorModal from "./ErrorModal";
import { contributeToGroup } from "../../api";
import { LoaderCircle } from "lucide-react";

const CardMomoContribution = ({ token }) => {
  const [contributionType, setContributionType] = useState("MOMO");
  const modalRef = useRef(null);
  const errorModalRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isContributing, setIsContributing] = useState(false);
  const [inputError, setInputError] = useState({
    field: "",
    errorMessage: "",
  });

  // Form States
  const [formData, setFormData] = useState({
    amount: "",
    cardName: "",
    cardNumber: "",
    expiry: "2024-06",
    cvv: "",
    phone: "",
    momoName: "",
    anonymous: false,
  });

  const handleInputChange = (field) => (e) => {
    let value =
      e.target.type === "checkbox" || e.target.type === "radio"
        ? e.target.checked
        : e.target.value;

    const numericFields = ["amount", "phone", "cardNumber", "cvv"];

    if (numericFields.includes(field)) {
      // 1. Always remove non-numeric characters first
      value = value.replace(/\D/g, "");

      // 2. Specific logic per field
      if (field === "amount") {
        // Remove leading zeros and cap at 10,000,000 rwf
        value = value.replace(/^0+/, "");
        const numValue = value === "" ? 0 : parseInt(value, 10);

        if (value !== "" && numValue < 100) {
          setInputError({
            field: "amount",
            errorMessage: "Minimum contribution is 100 RWF",
          });
        } else {
          // Clear error if they meet the requirement or field is empty
          setInputError({ field: "", errorMessage: "" });
        }
      } else if (field === "phone") {
        // Strip non-numeric and leading zeros
        value = value.replace(/\D/g, "").replace(/^0+/, "");

        // Limit to 9 digits (since the 0 is removed)
        value = value.slice(0, 9);

        if (value.length >= 2) {
          const prefix = value.slice(0, 2);
          const isMTN = prefix === "78" || prefix === "79";

          if (!isMTN) {
            setInputError({
              field: "phone",
              errorMessage: "We currently accept MTN numbers only.",
            });
          } else {
            // CLEAR the error once the prefix is valid
            setInputError({ field: "", errorMessage: "" });
          }
        } else {
          // Optional: Clear error if they delete back to 0 or 1 digit
          setInputError({ field: "", errorMessage: "" });
        }
      } else if (field === "cardNumber") {
        // Allow leading zeros (some cards start with 0) and limit to 16
        value = value.slice(0, 16);
      } else if (field === "cvv") {
        // Allow leading zeros (e.g., 012) and limit to 3
        value = value.slice(0, 3);
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFormInvalid =
    !formData.amount ||
    !formData.phone ||
    inputError.errorMessage ||
    (!formData.anonymous &&
      (!formData.momoName?.trim() || formData.momoName?.length < 3)) ||
    Number(formData.amount) < 100 ||
    formData.phone.length < 9;

  const handleContribute = async () => {
    if (isFormInvalid) return;
    setIsContributing(true);
    try {
      const result = await contributeToGroup(token, formData);
      console.log({ result });
      modalRef.current?.showModal();
    } catch (err) {
      // Handle error (e.g., show toast notification)
      console.error("Contribution Error:", { err });
      setErrorMessage(err.message);
      errorModalRef.current?.showModal();
    } finally {
      setIsContributing(false);
    }
  };

  return (
    <div>
      {/* navigational tabs  // justify-evenly gap-4 // */}
      <div className="flex border-b border-[#DEE2E7] dark:border-[#3D404B] mt-4 text-sm ">
        {/* <span
          onClick={() => setContributionType("Card")}
          className={`${
            contributionType === "Card"
              ? "border-b-4 border-defaultGreen py-2"
              : "py-2"
          } cursor-pointer`}
        >
          <h2
            className={`${
              contributionType === "Card"
                ? "font-bold"
                : "text-[#777C81] dark:text-[#CAD0D7]"
            }`}
          >
            Contribute with Credit/Debit Card
          </h2>
        </span> */}

        <span
          onClick={() => setContributionType("MOMO")}
          className={`${
            contributionType === "MOMO"
              ? "border-b-4 border-defaultGreen py-2"
              : "py-2"
          } cursor-pointer`}
        >
          <h2
            className={`${
              contributionType === "MOMO"
                ? "font-bold"
                : "text-[#777C81] dark:text-[#CAD0D7]"
            }`}
          >
            Contribute with MTN Mobile Money (MOMO)
          </h2>
        </span>
      </div>

      <div className="my-5">
        {contributionType === "Card" && (
          <div className="flex flex-col gap-y-2 my-4 mx-auto lg:mx-0 md:w-2/3">
            <div className="flex gap-3 items-center my-2">
              <VisaLogo />
              <MasterCardLogo />
            </div>
            <InputField
              label="Enter amount you want to contribute"
              placeholder="RWF"
              value={formData.amount}
              setValue={handleInputChange("amount")}
            />
            <InputField
              label="Name On Card"
              placeholder="Enter Full Name"
              value={formData.cardName}
              setValue={handleInputChange("cardName")}
            />
            <InputField
              label="Card Number"
              placeholder="1234 1234 1234 1234"
              value={formData.cardNumber}
              setValue={handleInputChange("cardNumber")}
            />
            <div className="flex gap-x-5 w-full justify-between">
              <InputField
                type="month"
                label="Expiration Date"
                value={formData.expiry}
                setValue={handleInputChange("expiry")}
              />
              <InputField
                type="password"
                label="CVV/CVC"
                placeholder="•••"
                value={formData.cvv}
                setValue={handleInputChange("cvv")}
              />
            </div>
            <div className="flex items-center my-2">
              <input
                id="anon-card"
                type="checkbox"
                className="w-4 h-4"
                checked={formData.anonymous}
                onChange={handleInputChange("anonymous")}
              />
              <label
                htmlFor="anon-card"
                className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300 cursor-pointer"
              >
                Contribute anonymously
              </label>
            </div>
            <button
              className="w-full text-white bg-defaultGreen font-medium rounded-lg text-sm px-5 py-2.5"
              onClick={() => modalRef.current?.showModal()}
            >
              Contribute
            </button>
          </div>
        )}

        {contributionType === "MOMO" && (
          <div className="flex flex-col gap-y-2 my-4 mx-auto lg:mx-0 md:w-2/3">
            <InputField
              label="Enter amount you want to contribute"
              placeholder="RWF"
              value={formData.amount}
              setValue={handleInputChange("amount")}
              error={inputError.field === "amount" && inputError.errorMessage}
            />
            <InputField
              type="number"
              label="Phone Number (MTN)"
              placeholder="7xxxxxxxxx"
              value={formData.phone}
              setValue={handleInputChange("phone")}
              error={inputError.field === "phone" && inputError.errorMessage}
            >
              <RwFlag className="h-6 w-6" /> +250
            </InputField>
            <div className="flex items-center my-2 ">
              <input
                id="anon-momo"
                type="checkbox"
                className="w-4 h-4"
                checked={formData.anonymous}
                onChange={handleInputChange("anonymous")}
              />
              <label
                htmlFor="anon-momo"
                className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300 cursor-pointer"
              >
                Contribute anonymously
              </label>
            </div>
            {!formData.anonymous && (
              <InputField
                label="Enter Your Name"
                placeholder="Enter Your Full Name"
                value={formData.momoName}
                setValue={handleInputChange("momoName")}
              />
            )}
            <button
              disabled={isFormInvalid || isContributing}
              className={`my-2 w-full text-white font-medium rounded-lg text-sm px-5 py-2.5 transition-colors flex items-center justify-center gap-4 ${
                isFormInvalid
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-defaultGreen hover:bg-opacity-90"
              }`}
              onClick={handleContribute}
            >
              {isContributing && <LoaderCircle className="animate-spin" />}{" "}
              Contribute
            </button>
          </div>
        )}
      </div>
      <ConfirmModal
        ref={modalRef}
        amount={formData.amount}
        phone={formData.phone}
      />
      <ErrorModal ref={errorModalRef} message={errorMessage} />
    </div>
  );
};

export default CardMomoContribution;
